import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import AlertIcon from "@atlaskit/icon/core/alert";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import ChartBarIcon from "@atlaskit/icon/core/chart-bar";
import IncidentIcon from "@atlaskit/icon/core/incident";

import { useUserContext } from "@/components/user-context/user-context-provider";
import { Link } from "@/libraries/design/components/link/src/link";

import { tabbedHeroSectionManageIncidentsMessages } from "../reusable-xflow-hero-component/tabbed-hero-section.messages.tsx";

import { ContentTypeImage } from "./content-type-image.tsx";

import type { TabProps, TabType } from "./tab-type.tsx";

type IncidentContentTypeProps = TabProps;

export const IncidentContentType: TabType = ({}: IncidentContentTypeProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { user } = useUserContext();

    return (
        <ContentTypeImage
            imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/7v45j3bosu45w2lq477u15tjfem5783e.webp"
            imageAlt="A list of 4 service requests with fields for summary, status, requestor, channel and response. There is a search bar above the list of requests."
            title={intl.formatMessage(
                tabbedHeroSectionManageIncidentsMessages.contentTitle
            )}
            description={intl.formatMessage(
                tabbedHeroSectionManageIncidentsMessages.contentDescription
            )}
            hideSeparator={true}
            iconCollection={[
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionManageIncidentsMessages.contentFeaturePoint1
                    ),
                    icon: <AlertIcon label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionManageIncidentsMessages.contentFeaturePoint2
                    ),
                    icon: <IncidentIcon label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionManageIncidentsMessages.contentFeaturePoint3
                    ),
                    icon: <CalendarIcon label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionManageIncidentsMessages.contentFeaturePoint4
                    ),
                    icon: <ChartBarIcon label="" />
                }
            ]}
            cta={
                // eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name
                <Link
                    href={"/software/jira/service-management"}
                    text={intl.formatMessage(
                        tabbedHeroSectionManageIncidentsMessages.contentCta
                    )}
                    type={"primary"}
                    dataEventProps={{
                        "data-product-key": "jiraServiceManagement",
                        "data-event-component": "linkButton",
                        "data-event-container": "tabsMenu",
                        "data-event": "clicked",
                        "data-label": "try-it-out",
                        "data-label-english": "Explore Jira Service Management",
                        "data-track-event":
                            "jsm-pt.content-types.manage-incidents.cta"
                    }}
                    onClick={() => {
                        createAnalyticsEvent({
                            eventName: "jsm-crossflow-tab-cta-clicked",
                            eventType: "ui",
                            action: "clicked",
                            actionSubject: "linkButton",
                            actionSubjectId: "jsmCrossFlowHeroSection",
                            attributes: {
                                reactComponent: "IncidentContentType",
                                tab: "manage-incidents",
                                userTeamType:
                                    user["extended_profile"]?.team_type
                            }
                        }).fire();
                    }}
                />
            }
            reverseLayout
        />
    );
};
